// components/CreatePlanModal.jsx
import React, { useState } from 'react';
import { X, Calendar, Percent, ChevronDown, ChevronUp, Gift, Eye, EyeOff, AlertCircle } from 'lucide-react';

const CreatePlanModal = ({ 
  isOpen, 
  onClose, 
  formState, 
  onChange, 
  onSubmit, 
  isSubmitting,
  error // ✅ New prop for error display
}) => {
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    pricing: true,
    features: false,
    trial: false
  });

  if (!isOpen) return null;

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const calculateTaxBreakdown = () => {
    const price = parseFloat(formState.annualPrice) || 0;
    const taxRate = parseFloat(formState.taxPercentage) || 0;
    if (price === 0 || taxRate === 0) return null;

    if (formState.isTaxInclusive) {
      const baseAmount = price / (1 + taxRate / 100);
      const taxAmount = price - baseAmount;
      return { type: 'inclusive', totalPrice: price, baseAmount, taxAmount, taxRate, taxName: formState.taxName || 'GST' };
    } else {
      const baseAmount = price;
      const taxAmount = price * (taxRate / 100);
      const totalPrice = price + taxAmount;
      return { type: 'exclusive', totalPrice, baseAmount, taxAmount, taxRate, taxName: formState.taxName || 'GST' };
    }
  };

  const taxBreakdown = calculateTaxBreakdown();

  const SectionHeader = ({ title, section, icon: Icon }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between py-2 text-left"
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-indigo-500" />
        <span className="text-sm font-medium text-slate-700">{title}</span>
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="w-4 h-4 text-slate-400" />
      ) : (
        <ChevronDown className="w-4 h-4 text-slate-400" />
      )}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-lg font-semibold text-slate-900">Create New Plan</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        {/* Body */}
        <form onSubmit={onSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          
          {/* ✅ Error Display */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-700 font-medium">Error</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
              <button 
                type="button"
                onClick={() => onClose()} 
                className="text-red-500 hover:text-red-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Basic Info Section */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <SectionHeader title="Basic Information" section="basic" icon={Calendar} />
            {expandedSections.basic && (
              <div className="space-y-3 mt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Plan Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formState.name}
                      onChange={onChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                      placeholder="e.g., Professional"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
                    <select
                      name="type"
                      value={formState.type}
                      onChange={onChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    >
                      <option value="free">Free</option>
                      <option value="paid">Paid</option>
                      <option value="enterprise">Enterprise</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Description *</label>
                  <textarea
                    name="description"
                    value={formState.description}
                    onChange={onChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    rows="2"
                    placeholder="Describe what this plan includes..."
                    required
                  />
                </div>
              </div>
            )}
          </div>

          {/* Pricing Section */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <SectionHeader title="Pricing & Tax" section="pricing" icon={Percent} />
            {expandedSections.pricing && (
              <div className="space-y-3 mt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Annual Price (₹)</label>
                    <input
                      type="number"
                      name="annualPrice"
                      min="0"
                      step="1"
                      value={formState.annualPrice}
                      onChange={onChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                      placeholder="0 = Free"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Tax Name</label>
                    <input
                      type="text"
                      name="taxName"
                      value={formState.taxName}
                      onChange={onChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                      placeholder="e.g., GST"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Tax Percentage (%)</label>
                    <input
                      type="number"
                      name="taxPercentage"
                      min="0"
                      max="100"
                      step="0.1"
                      value={formState.taxPercentage}
                      onChange={onChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                      placeholder="0"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        name="isTaxInclusive"
                        checked={formState.isTaxInclusive}
                        onChange={onChange}
                        className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                      />
                      Price includes tax
                    </label>
                  </div>
                </div>

                {taxBreakdown && (
                  <div className={`mt-2 p-3 rounded-lg border ${
                    taxBreakdown.type === 'inclusive' ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'
                  }`}>
                    <p className={`text-xs ${taxBreakdown.type === 'inclusive' ? 'text-blue-700' : 'text-amber-700'}`}>
                      <span className="font-semibold">Tax Breakdown:</span><br />
                      {taxBreakdown.type === 'inclusive' ? (
                        <>
                          Total: ₹{taxBreakdown.totalPrice.toFixed(2)} (incl. {taxBreakdown.taxRate}% {taxBreakdown.taxName})<br />
                          Base: ₹{taxBreakdown.baseAmount.toFixed(2)} | Tax: ₹{taxBreakdown.taxAmount.toFixed(2)}
                        </>
                      ) : (
                        <>
                          Base: ₹{taxBreakdown.baseAmount.toFixed(2)}<br />
                          Tax: ₹{taxBreakdown.taxAmount.toFixed(2)} | Total: ₹{taxBreakdown.totalPrice.toFixed(2)}
                        </>
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Features Section */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <SectionHeader title="Features & Limits" section="features" icon={Calendar} />
            {expandedSections.features && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Max Students</label>
                  <input
                    type="number"
                    name="maxStudents"
                    min="0"
                    value={formState.maxStudents}
                    onChange={onChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Max Staff</label>
                  <input
                    type="number"
                    name="maxStaff"
                    min="0"
                    value={formState.maxStaff}
                    onChange={onChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Max Branches</label>
                  <input
                    type="number"
                    name="maxBranches"
                    min="1"
                    value={formState.maxBranches}
                    onChange={onChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    placeholder="1"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Storage (GB)</label>
                  <input
                    type="number"
                    name="maxStorageGB"
                    min="0"
                    value={formState.maxStorageGB}
                    onChange={onChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    placeholder="5"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Trial Section */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <SectionHeader title="Trial Settings" section="trial" icon={Gift} />
            {expandedSections.trial && (
              <div className="space-y-3 mt-2">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    name="trialEnabled"
                    checked={formState.trialEnabled}
                    onChange={onChange}
                    className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                  />
                  Enable Free Trial
                </label>
                {formState.trialEnabled && (
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Trial Duration (Days)</label>
                    <input
                      type="number"
                      name="trialDays"
                      min="1"
                      value={formState.trialDays}
                      onChange={onChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Status Toggles */}
          <div className="flex flex-wrap items-center gap-4 pt-2">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                name="isActive"
                checked={formState.isActive}
                onChange={onChange}
                className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
              />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                name="isPublic"
                checked={formState.isPublic}
                onChange={onChange}
                className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
              />
              <Eye className="w-4 h-4 text-slate-400" />
              Public (visible to all tenants)
            </label>
            {!formState.isPublic && (
              <span className="text-xs text-amber-600 flex items-center gap-1">
                <EyeOff className="w-3.5 h-3.5" />
                Private plan - only assigned tenants can see this
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Creating...' : 'Create Plan'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePlanModal;